import {
  DEFAULT_DATE_FORMAT_FNS,
  FilterKeys,
  GetStaffQueryFilters,
  STAFF_ARRANGE_BY_FILTER_KEYS,
} from '@schooly/api';
import {
  getSelectedItemsWithGrouping,
  SelectedItem,
  SelectedItemWithGrouping,
} from '@schooly/components/filters';
import { Genders, Nationalities, SchoolUserRole } from '@schooly/constants';
import { useAgeGroups } from '@schooly/hooks/use-school-properties';
import { format } from 'date-fns';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { useSchool } from '../../hooks/useSchool';

type UseStaffFiltersProps = {
  schoolId: string;
  filters: GetStaffQueryFilters;
  onSetFilters: (filters: GetStaffQueryFilters) => void;
};

export const useStaffFilters = ({ schoolId, filters, onSetFilters }: UseStaffFiltersProps) => {
  const ageGroups = useAgeGroups({
    schoolId: schoolId,
    userType: SchoolUserRole.Staff,
  });
  const { getAgeGroupById, schoolLevelsWithAgeGroupsMap } = ageGroups;
  const { hasHouses, hasDepartments } = useSchool();
  const { formatMessage } = useIntl();

  const actions = useMemo(() => {
    const updateFilter =
      (
        key:
          | FilterKeys.House
          | FilterKeys.Department
          | FilterKeys.AgeGroup
          | FilterKeys.Status
          | FilterKeys.Subject,
      ) =>
      (v: string[]) => {
        onSetFilters({
          ...filters,
          [key]: v,
        });
      };
    const clearFilter = (key: keyof typeof filters) => () => {
      onSetFilters({
        ...filters,
        [key]: [],
      });
    };

    const onSetNationality = (v: Nationalities[]) => {
      onSetFilters({
        ...filters,
        [FilterKeys.Nationality]: v,
      });
    };
    const onSetGender = (v: Genders[]) => {
      onSetFilters({
        ...filters,
        [FilterKeys.Gender]: v,
      });
    };

    return {
      onSetDate: (v: Date) =>
        onSetFilters({ ...filters, [FilterKeys.Date]: [format(v, DEFAULT_DATE_FORMAT_FNS)] }),
      onSetGender,
      onSetNationality,
      onSetHouseId: updateFilter(FilterKeys.House),
      onClearHouse: clearFilter(FilterKeys.House),
      onSetAgeGroup: updateFilter(FilterKeys.AgeGroup),
      onClearAgeGroup: clearFilter(FilterKeys.AgeGroup),
      onSetStatusId: updateFilter(FilterKeys.Status),
      onClearStatus: clearFilter(FilterKeys.Status),
      onSetDepartmentId: updateFilter(FilterKeys.Department),
      onClearDepartment: clearFilter(FilterKeys.Department),
      onSetSubjectId: updateFilter(FilterKeys.Subject),
      onClearSubject: clearFilter(FilterKeys.Subject),
      onClearNationality: clearFilter(FilterKeys.Nationality),
      onClearGender: clearFilter(FilterKeys.Gender),
    };
  }, [filters, onSetFilters]);

  const filtersDate = filters.date?.[0];
  const filtersStatus = filters.status;
  const filtersNationality = filters.nationality;
  const filtersGender = filters.gender;
  const filtersHouse = filters.house;
  const filtersAgeGroup = filters.age_group;
  const filtersDepartment = filters.department;
  const filtersSubject = filters.subject;

  const labels = {
    [FilterKeys.Date]: formatMessage({ id: 'schoolProperty-Date' }),
    [FilterKeys.Status]: formatMessage({ id: 'schoolProperty-Status' }),
    [FilterKeys.AgeGroup]: formatMessage({ id: 'schoolProperty-AgeGroup' }),
    [FilterKeys.House]: formatMessage({ id: 'schoolProperty-House' }),
    [FilterKeys.Department]: formatMessage({ id: 'schoolProperty-Department' }),
    [FilterKeys.Subject]: formatMessage({ id: 'groups-GroupSubject' }),
    [FilterKeys.Gender]: formatMessage({ id: 'peopleDetail-Gender' }),
    [FilterKeys.Nationality]: formatMessage({ id: 'peopleDetail-Nationality' }),
    arrangeBy: formatMessage({ id: 'filter-ArrangeBy' }),
  };

  const arrangeByDropdownOptions = hasHouses
    ? STAFF_ARRANGE_BY_FILTER_KEYS
    : STAFF_ARRANGE_BY_FILTER_KEYS.filter((k) => k !== FilterKeys.House);

  const selectedItemsForAgeGroups: SelectedItemWithGrouping[] = useMemo(() => {
    const selectedAgeGroups =
      filtersAgeGroup?.reduce<SelectedItem[]>((acc, id) => {
        const ageGroup = getAgeGroupById(id);

        return ageGroup
          ? [
              ...acc,
              {
                id: ageGroup.id,
                groupId: ageGroup.level_id,
              },
            ]
          : acc;
      }, []) ?? [];

    return getSelectedItemsWithGrouping(selectedAgeGroups, schoolLevelsWithAgeGroupsMap);
  }, [filtersAgeGroup, schoolLevelsWithAgeGroupsMap, getAgeGroupById]);

  return {
    ...ageGroups,
    ...actions,
    labels,
    hasHouses,
    hasDepartments,
    arrangeByDropdownOptions,
    selectedItemsForAgeGroups,
    filtersDate,
    filtersAgeGroup,
    filtersStatus,
    filtersNationality,
    filtersGender,
    filtersHouse,
    filtersDepartment,
    filtersSubject,
  };
};
